/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React from "react";
import MediaPrepHeaderSection from "./mediaPrepheaderSection";
import MediaPrepPageHeader from "./mediaPrepPageHeader";
import { useTranslation } from "react-i18next";

interface Props {
  mediaSolutions: any[];
  categories: any[];
  manufacturers: any[];

  formData: {
    selectedMediaSolution: string;
    productName: string;
    categoryId: string;
    manufacturerId: string;
    catalogNumber: string;
    shelfLife: string;
  };

  errors: {
    selectedMediaSolution?: string;
    productName?: string;
    categoryId?: string;
    manufacturerId?: string;
    catalogNumber?: string;
    shelfLife?: string;
    referenceTemp?: string;
  };

  onChange: (field: string, value: any) => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
}

const SolutionProductDefinitionForm: React.FC<Props> = ({
  mediaSolutions,
  categories,
  manufacturers,
  formData,
  errors,
  onChange,
  onSave,
  onCancel,
  isSaving,
}) => {
  const { t } = useTranslation();

  return (
    <div className="media-prep-scope">
      <MediaPrepHeaderSection />
      <MediaPrepPageHeader
        pageHeader={t("mediaSolutionProductMaster.title")}
      />

      <div className="flex flex-col h-full bg-[#F3F6F9]">
        <div className="p-4 overflow-y-auto flex-1">
          <div className="px-1 pb-13 pt-0 max-w-full animate-in fade-in duration-500 min-h-full">

            {/* SECTION 1 */}
            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden mb-1">
              <div className="px-10 pt-12 pb-4 space-y-2">

                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-xs font-black -mt-1">01</div>

                  <h3 className="text-sm font-black text-slate-800 tracking-tight uppercase">
                    {t('mediaSolutionProductMaster.form.ProductIdentification')}
                  </h3>
                </div>
                <div className="pl-14 grid grid-cols-1 md:grid-cols-6 gap-3">
                {/* MEDIA SOLUTION */}
                <div className="space-y-2 md:col-span-3 -mt-1">
                  <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest block">
                    {t('mediaSolutionProductMaster.form.MediaSolution')} <span className="text-red-500">*</span>
                  </label>

                  <div className="relative group">
                    <select
                      value={formData.selectedMediaSolution}
                      onChange={(e) =>
                        onChange("selectedMediaSolution", e.target.value)
                      }
                      className="w-full bg-slate-50 border border-transparent rounded-xl px-4 py-2 text-sm font-medium text-slate-600 appearance-none outline-none focus:bg-white focus:border-orange-500 transition-all cursor-pointer"
                    >
                      <option value="">{t('mediaSolutionProductMaster.form.mediaSelectionPlaceholder')}</option>

                      {mediaSolutions.map((item: any) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>

                    <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none">
                      {t('mediaPowderProductMaster.icons.expand_more')}
                    </span>
                  </div>

                  {errors.selectedMediaSolution && (
                    <p className="errorText">
                      {errors.selectedMediaSolution}
                    </p>
                  )}
                </div>

                {/* PRODUCT NAME */}
                <div className="space-y-1 md:col-span-3 -mt-1">
                  <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest block">
                    {t('mediaSolutionProductMaster.form.productName')} <span className="text-red-500">*</span>
                  </label>

                  <input
                    value={formData.productName}
                    onChange={(e) =>
                      onChange("productName", e.target.value)
                    }
                    type="text"
                    placeholder="e.g. Antibiotic Supplement G"
                    className="w-full bg-slate-50 border border-transparent rounded-xl px-4 py-2 text-sm font-medium text-slate-700 outline-none focus:bg-white focus:border-orange-500 transition-all"
                  />

                  {errors.productName && (
                    <p className="errorText">{errors.productName}</p>
                  )}
                </div>

                {/* CATEGORY + MANUFACTURER */}
                  {/* CATEGORY */}
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest block">
                      {t('mediaSolutionProductMaster.form.category')} <span className="text-red-500">*</span>
                    </label>

                    <div className="relative group">
                      <select
                        value={formData.categoryId}
                        onChange={(e) =>
                          onChange("categoryId", e.target.value)
                        }
                        className="w-full bg-slate-50 border border-transparent rounded-xl px-4 py-2 text-sm font-medium text-slate-600 appearance-none outline-none focus:bg-white focus:border-orange-500 transition-all cursor-pointer"
                      >
                        <option value="">{t('mediaSolutionProductMaster.form.categoryPlaceholder')}</option>

                        {categories.map((item: any) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>

                      <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none">
                        {t('mediaPowderProductMaster.icons.expand_more')}
                      </span>
                    </div>

                    {errors.categoryId && (
                      <p className="errorText">{errors.categoryId}</p>
                    )}
                  </div>

                  {/* MANUFACTURER */}
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest block">
                      {t('mediaSolutionProductMaster.form.manufacturer')} <span className="text-red-500">*</span>
                    </label>

                    <div className="relative group">
                      <select
                        value={formData.manufacturerId}
                        onChange={(e) =>
                          onChange("manufacturerId", e.target.value)
                        }
                        className="w-full bg-slate-50 border border-transparent rounded-xl px-4 py-2 text-sm font-medium text-slate-600 appearance-none outline-none focus:bg-white focus:border-orange-500 transition-all cursor-pointer"
                      >
                        <option value="">{t('mediaSolutionProductMaster.form.manufacturerPlaceholder')}</option>

                        {manufacturers.map((item: any) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>

                      <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none">
                        {t('mediaPowderProductMaster.icons.expand_more')}
                      </span>
                    </div>

                    {errors.manufacturerId && (
                      <p className="errorText">{errors.manufacturerId}</p>
                    )}
                  </div>

                {/* CATALOG */}
                <div className="space-y-1 md:col-span-2 -mt-0.5">
                  <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest block">
                    {t('mediaSolutionProductMaster.form.catalogNumber')} <span className="text-red-500">*</span>
                  </label>

                  <input
                    value={formData.catalogNumber}
                    onChange={(e) =>
                      onChange("catalogNumber", e.target.value)
                    }
                    type="text"
                    placeholder={t('mediaSolutionProductMaster.form.catalogPlaceholder')}
                    className="w-full bg-slate-50 border border-transparent rounded-xl px-4 py-2 text-sm font-medium text-slate-700 outline-none focus:bg-white focus:border-orange-500 transition-all"
                  />

                  {errors.catalogNumber && (
                    <p className="errorText">{errors.catalogNumber}</p>
                  )}
                </div>
              </div>
            </div>

            {/* SECTION 2 */}
            <div className="border-t border-slate-100">
              <div className="pt-1 pb-2 px-10 space-y-2">

                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-xs font-black -mt-1">02</div>

                  <h3 className="text-sm font-black text-slate-800 tracking-tight uppercase">
                    {t('mediaSolutionProductMaster.form.shelfLifeStability')}
                  </h3>
                </div>

                <div className="pl-14 w-full md:w-1/3 space-y-2">
                  <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest block">
                    {t('mediaSolutionProductMaster.form.shelfLife')}{" "}
                    <span className="text-red-500">*</span>
                  </label>

                  <input
                    value={formData.shelfLife}
                    onChange={(e) =>
                      onChange("shelfLife", e.target.value)
                    }
                    type="number"
                    placeholder={t('mediaSolutionProductMaster.form.shelfLifePlaceholder')}
                    className="w-full bg-slate-50 border border-transparent rounded-xl px-4 py-2 text-sm font-medium text-slate-700 outline-none focus:bg-white focus:border-orange-500 transition-all"
                  />

                  {errors.shelfLife && (<p className="errorText">{errors.shelfLife}</p>)}
                </div>
              </div>
            </div>

            {/* ACTIONS */}
            <div className="px-10 py-3 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button
                onClick={onCancel}
                className="px-10 py-3 bg-white border border-transparent rounded-xl text-[12px] font-black text-slate-500 uppercase tracking-widest hover:bg-slate-100 active:scale-95 transition-all shadow-sm"
              >
                {t('mediaSolutionProductMaster.form.Cancel')}
              </button>

              <button
                onClick={onSave}
                disabled={isSaving}
                className="px-10 py-3 bg-[#FF7043] text-white rounded-xl text-[12px] font-black border border-transparent uppercase tracking-widest hover:brightness-110 active:scale-95 transition-all shadow-lg shadow-orange-500/20"
              >
                {t('mediaSolutionProductMaster.form.Save')}
              </button>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SolutionProductDefinitionForm;